import { z } from 'zod';

/** Pontos de um trajeto depois de expandir a volta: origem + até 3 destinos. É a regra da tela; o
 *  expenses-api recusa só acima de 4 pernas (`refundKMMaxSegmentsExceeded`). */
export const KM_MAX_POINTS = 4;
/** A data do trajeto vai de hoje até 30 dias atrás: a validade do voucher de KM. */
export const KM_DATE_WINDOW_DAYS = 30;

export const KmPlaceSchema = z
  .object({ lat: z.number(), lng: z.number(), address: z.string().min(1) })
  .strict();
export type KmPlace = z.infer<typeof KmPlaceSchema>;

export const KmNamedRefSchema = z.object({ id: z.string().min(1), name: z.string() }).strict();
export type KmNamedRef = z.infer<typeof KmNamedRefSchema>;

export const KmLegSchema = z
  .object({
    segmentId: z.string(),
    from: KmPlaceSchema,
    to: KmPlaceSchema,
    /** KM que vale (manual se houver, senão o calculado), 2 casas. */
    km: z.number(),
    calculatedKm: z.number(),
    manualKm: z.number().nullable(),
    valuePerKm: z.number(),
    amount: z.number(),
    /** Calculado = 0 e sem KM manual: rota não calculada, bloqueia o envio. */
    routeUnavailable: z.boolean(),
    /** KM manual com `setOutsidePolicyManualEditing` ligado na política. */
    outOfPolicy: z.boolean(),
    /** O traçado pelas ruas da rota que gerou o km (encoded polyline do Google, precisão 5).
     *  Ausente quando o cálculo não o devolveu: o mapa desenha a curva tracejada. */
    polyline: z.string().min(1).optional(),
  })
  .strict();
export type KmLeg = z.infer<typeof KmLegSchema>;

export const KmRouteSchema = z
  .object({
    routeId: z.string(),
    /** `YYYY-MM-DD` no fuso do usuário. */
    date: z.iso.date(),
    legs: z.array(KmLegSchema),
  })
  .strict();
export type KmRoute = z.infer<typeof KmRouteSchema>;

export const KmCorporateSchema = z
  .object({
    traveler: KmNamedRefSchema,
    company: KmNamedRefSchema,
    costCenter: KmNamedRefSchema,
    project: KmNamedRefSchema.nullable(),
    motive: KmNamedRefSchema.nullable(),
    obs: z.string().max(300).nullable(),
  })
  .strict();
export type KmCorporate = z.infer<typeof KmCorporateSchema>;

export const KmDraftSchema = z
  .object({
    refundId: z.string(),
    corporate: KmCorporateSchema,
    routes: z.array(KmRouteSchema),
    totals: z.object({ km: z.number(), amount: z.number(), legs: z.number().int() }).strict(),
    canSubmit: z.boolean(),
  })
  .strict();
export type KmDraft = z.infer<typeof KmDraftSchema>;

// Os corpos de pedido toleram chave desconhecida (um web-chat mais novo que o BFF não morre no
// 400); as respostas que o BFF monta são estritas.
export const KmCorporateBodySchema = z.looseObject({
  /** Ausente = o próprio usuário. */
  travelerId: z.string().min(1).optional(),
  companyId: z.string().min(1),
  costCenterId: z.string().min(1),
  projectId: z.string().min(1).optional(),
  motiveId: z.string().min(1).optional(),
  obs: z.string().max(300).optional(),
});
export type KmCorporateBody = z.infer<typeof KmCorporateBodySchema>;

export const KmRouteBodySchema = z.looseObject({
  /** Ausente = trajeto novo. */
  routeId: z.string().min(1).optional(),
  date: z.iso.date(),
  /** A, paradas, B — sem a volta, que `roundTrip` acrescenta. */
  points: z.array(KmPlaceSchema).min(2),
  roundTrip: z.boolean(),
});
export type KmRouteBody = z.infer<typeof KmRouteBodySchema>;

export const KmDistanceBodySchema = z.looseObject({ km: z.number().positive().max(10000) });
export type KmDistanceBody = z.infer<typeof KmDistanceBodySchema>;

export const KmOptionsSchema = z
  .object({
    defaultPrice: z.number(),
    pricePerCity: z.array(z.object({ city: z.string(), price: z.number() }).strict()),
    manualEditOutOfPolicy: z.boolean(),
    motiveRequired: z.boolean(),
    projectRequired: z.boolean(),
    /** O aparato corporativo do viajante, no formato `CorporateBookingForm` do genui-contract. */
    form: z.unknown(),
  })
  .strict();
export type KmOptions = z.infer<typeof KmOptionsSchema>;

export const KmSubmitResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('submitted'), legs: z.array(KmLegSchema) }).strict(),
  z.object({ status: z.literal('indeterminate') }).strict(),
]);
export type KmSubmitResponse = z.infer<typeof KmSubmitResponseSchema>;

/** Códigos de erro do fio (`{ error: KmRefundErrorCode, message?: string }`). */
export const KM_REFUND_ERROR_CODES = [
  'identity_not_found',
  'km_refund_not_enabled',
  'not_found',
  'invalid_body',
  'too_many_points',
  'date_out_of_window',
  'invalid_place',
  'traveler_not_allowed',
  'project_required',
  'motive_required',
  'route_unavailable',
  'bank_data_missing',
  'bank_data_unavailable',
  'submit_in_flight',
  'business_rejected',
  'km_refund_unavailable',
  'transit_open',
  'no_transit',
  'invalid_dates',
] as const;
export type KmRefundErrorCode = (typeof KM_REFUND_ERROR_CODES)[number];
